/**
 * @file: useTaskInspectionScore.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useFetchAsyncState } from '@euler/functions/useFetchAsyncState';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useEffect } from 'react';

const kUseCache = true;
const kCacheExpiryInSeconds = 600; /** 10 minutes */

type ScoreInfo = { score: number; timestamp: number };

export const useTaskInspectionScore = (
  taskNo: string,
  options: {
    defaultScore?: number;
    refresh?: boolean;
  },
) => {
  const { taskService, defaultStorageService } = useServiceFactory();

  const query = usePersistFn(async () => {
    const key = `task:${taskNo}:inspection-score`;
    const res =
      kUseCache && !options.refresh
        ? await defaultStorageService.get<ScoreInfo>(key)
        : null;
    if (
      res != null &&
      Date.now() - res.timestamp < kCacheExpiryInSeconds * 1000
    ) {
      return res.score;
    }
    try {
      const value = await taskService.getInspectionScore(taskNo, {
        refresh: options.refresh,
      });
      try {
        // save to cache.
        await defaultStorageService.set<ScoreInfo>(key, {
          score: value,
          timestamp: Date.now(),
        });
      } catch (e) {
        console.error(e);
      }

      return value;
    } catch (e) {
      console.error(e);
      return options?.defaultScore;
    }
  });

  const [state, fetchState] = useFetchAsyncState(query, {
    status: 'loading',
  });

  useEffect(() => {
    fetchState().catch(onErrorIgnore);
  }, [fetchState]);

  return state;
};
